import { Modal } from "bootstrap";
import { applyCsrf, ensureCsrf } from "./csrf";

type TurnstileWidgetId = string;

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      theme: string;
      "expired-callback": () => void;
      "error-callback": () => void;
    },
  ): TurnstileWidgetId;
  getResponse(widgetId: TurnstileWidgetId): string;
  reset(widgetId: TurnstileWidgetId): void;
}

interface ReportWindow extends Window {
  turnstile?: TurnstileApi;
  sec_token?: string;
  sec_token_name?: string;
}

interface ReportResponse {
  success?: boolean;
  errors?: Record<string, string>;
  message?: string;
  csrf_token?: string;
  csrf_hash?: string;
}

const validEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const successCloseDelay = 5000;

export function initializeReportCommentModal(): void {
  const modalElement = document.getElementById("reportCommentModal");
  const form = document.getElementById(
    "report-comment-form",
  ) as HTMLFormElement | null;

  if (!modalElement || !form || modalElement.dataset.initialized === "true") {
    return;
  }

  modalElement.dataset.initialized = "true";

  const reportWindow = window as ReportWindow;
  const modal = Modal.getOrCreateInstance(modalElement);
  const modalBody = modalElement.querySelector<HTMLElement>(".modal-body");
  const steps = Array.from(
    modalElement.querySelectorAll<HTMLElement>(".report-step"),
  );
  const privacyNotice = modalElement.querySelector<HTMLElement>(
    ".report-privacy-notice",
  );
  const privacyDivider = modalElement.querySelector<HTMLElement>(
    ".report-privacy-divider",
  );
  const successBox = modalElement.querySelector<HTMLElement>(".report-success");
  const generalError =
    modalElement.querySelector<HTMLElement>(".report-form-error");
  const otherCheckbox = document.getElementById(
    "report-reason-other",
  ) as HTMLInputElement | null;
  const otherHint =
    modalElement.querySelector<HTMLElement>(".report-other-hint");
  const submitButton = document.getElementById(
    "report-submit-btn",
  ) as HTMLButtonElement | null;

  let currentStep = 1;
  let lastTriggerButton: HTMLButtonElement | null = null;
  let reportWidgetId: TurnstileWidgetId | null = null;
  let submitting = false;
  let closeTimer: number | null = null;
  let activeRequest: AbortController | null = null;
  let requestGeneration = 0;

  const getTurnstile = (): TurnstileApi | undefined => reportWindow.turnstile;

  const getCsrfField = (): HTMLInputElement | null =>
    document.getElementById("report-csrf-field") as HTMLInputElement | null;

  const clearErrors = (): void => {
    modalElement
      .querySelectorAll<HTMLElement>(".report-field-error")
      .forEach((element) => {
        element.textContent = "";
        element.classList.remove("d-block");
      });
    modalElement
      .querySelectorAll<HTMLElement>(".is-invalid")
      .forEach((element) => element.classList.remove("is-invalid"));

    if (generalError) {
      generalError.textContent = "";
      generalError.classList.add("d-none");
    }
  };

  const getField = (fieldName: string): HTMLElement | null => {
    const selectorName = fieldName === "reason" ? "reason[]" : fieldName;

    return form.querySelector<HTMLElement>(`[name="${selectorName}"]`);
  };

  const showFieldError = (fieldName: string, message: string): void => {
    const errorId = `error-${fieldName.replace(/[[\]_]/g, "-")}`;
    const errorElement = document.getElementById(errorId);
    const field = getField(fieldName);

    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.add("d-block");
    }

    field?.classList.add("is-invalid");
  };

  const showGeneralError = (message: string): void => {
    if (!generalError) {
      return;
    }

    generalError.textContent = message;
    generalError.classList.remove("d-none");
  };

  const resetReportTurnstile = (): void => {
    const turnstile = getTurnstile();

    if (turnstile && reportWidgetId !== null) {
      turnstile.reset(reportWidgetId);
    }
  };

  const ensureReportTurnstile = (): void => {
    const container = document.getElementById("report-comment-turnstile");
    const turnstile = getTurnstile();

    if (!container || !turnstile || reportWidgetId !== null) {
      return;
    }

    const sitekey = container.dataset.sitekey;

    if (!sitekey) {
      return;
    }

    reportWidgetId = turnstile.render(container, {
      sitekey,
      theme: "light",
      "expired-callback": () => {
        showFieldError(
          "cf-turnstile-response",
          "Verification expired. Please complete it again.",
        );
      },
      "error-callback": () => {
        showFieldError(
          "cf-turnstile-response",
          "Verification could not be loaded. Please try again.",
        );
      },
    });
  };

  const focusFirstControl = (step: number): void => {
    const stepElement = steps.find(
      (element) => Number(element.dataset.step) === step,
    );
    const firstControl = stepElement?.querySelector<HTMLElement>(
      'input:not([type="hidden"]), textarea, select, button',
    );

    firstControl?.focus({ preventScroll: true });
  };

  const showStep = (step: number, focus = true): void => {
    steps.forEach((element) => {
      element.classList.toggle("d-none", Number(element.dataset.step) !== step);
    });

    currentStep = step;
    clearErrors();
    privacyNotice?.classList.toggle("d-none", step !== 1);
    privacyDivider?.classList.toggle("d-none", step !== 1);

    if (modalBody) {
      modalBody.scrollTop = 0;
    }

    if (step === 3) {
      ensureReportTurnstile();
    }

    if (focus) {
      focusFirstControl(step);
    }
  };

  const clearCurrentStep = (): void => {
    const stepElement = steps.find(
      (element) => Number(element.dataset.step) === currentStep,
    );

    stepElement
      ?.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >('input:not([type="hidden"]), textarea, select')
      .forEach((element) => {
        if (
          element instanceof HTMLInputElement &&
          (element.type === "checkbox" || element.type === "radio")
        ) {
          element.checked = false;
        } else {
          element.value = "";
        }
      });

    if (currentStep === 3) {
      resetReportTurnstile();
    }

    otherHint?.classList.add("d-none");
    clearErrors();
  };

  const validateStep = (step: number): boolean => {
    clearErrors();
    let firstInvalid: HTMLElement | null = null;

    const invalidate = (fieldName: string, message: string): void => {
      showFieldError(fieldName, message);
      firstInvalid ??= getField(fieldName);
    };

    if (step === 1) {
      const email = form.elements.namedItem(
        "reporter_email",
      ) as HTMLInputElement | null;
      const directlyAffected = form.querySelector<HTMLInputElement>(
        '[name="directly_affected"]:checked',
      );

      if (
        !email ||
        !validEmailRegex.test(email.value.trim()) ||
        email.value.trim().length > 254
      ) {
        invalidate("reporter_email", "Please enter a valid email address.");
      }

      if (!directlyAffected) {
        invalidate(
          "directly_affected",
          "Please indicate whether you are directly affected.",
        );
      }
    }

    if (step === 2) {
      const type = form.querySelector<HTMLInputElement>(
        '[name="type"]:checked',
      );
      const reasons = form.querySelectorAll<HTMLInputElement>(
        '[name="reason[]"]:checked',
      );
      const description = form.elements.namedItem(
        "description",
      ) as HTMLTextAreaElement | null;

      if (!type) {
        invalidate("type", "Please select what you are reporting.");
      }
      if (reasons.length === 0) {
        invalidate("reason", "Please select at least one reason.");
      }
      if (!description?.value.trim()) {
        invalidate("description", "Please provide a description.");
      } else if (description.value.trim().length > 1000) {
        invalidate("description", "Description cannot exceed 1000 characters.");
      }
    }

    firstInvalid?.focus();
    return firstInvalid === null;
  };

  const resetWizard = (): void => {
    const csrfField = getCsrfField();
    const csrfName = csrfField?.name;
    const csrfHash = csrfField?.value;

    form.reset();

    if (csrfField && csrfName && csrfHash) {
      csrfField.name = csrfName;
      csrfField.value = csrfHash;
    }

    submitting = false;
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = "Submit Report";
    }
    successBox?.classList.add("d-none");
    form.classList.remove("d-none");
    modalBody?.classList.remove("report-show-success");
    otherHint?.classList.add("d-none");
    showStep(1, false);
  };

  const stepForErrors = (errors: Record<string, string>): number => {
    if ("reporter_email" in errors || "directly_affected" in errors) {
      return 1;
    }
    if ("type" in errors || "reason" in errors || "description" in errors) {
      return 2;
    }

    return 3;
  };

  const handleResponse = (status: number, data: ReportResponse): void => {
    applyCsrf(data);

    if (status === 200 && data.success) {
      form.classList.add("d-none");
      privacyNotice?.classList.add("d-none");
      privacyDivider?.classList.add("d-none");
      modalBody?.classList.add("report-show-success");
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
      successBox?.classList.remove("d-none");
      successBox?.focus({ preventScroll: true });
      resetReportTurnstile();

      closeTimer = window.setTimeout(() => modal.hide(), successCloseDelay);
      return;
    }

    if (status === 403) {
      showStep(3);
      showFieldError(
        "cf-turnstile-response",
        "Your session expired. Please refresh the page and try again.",
      );
      resetReportTurnstile();
      return;
    }

    if (status === 422 && data.errors) {
      const errorStep = stepForErrors(data.errors);
      showStep(errorStep);

      Object.entries(data.errors).forEach(([field, message]) => {
        if (field === "comment_id" || field === "phone_number") {
          showGeneralError(message);
          return;
        }
        showFieldError(field, message);
      });

      resetReportTurnstile();
      return;
    }

    showStep(3);
    showGeneralError(data.message || "Something went wrong. Please try again.");
    resetReportTurnstile();
  };

  document.addEventListener("click", (event) => {
    const target = event.target as Element | null;
    const trigger = target?.closest<HTMLButtonElement>(".report-comment-btn");

    if (!trigger) {
      return;
    }

    event.preventDefault();
    requestGeneration++;
    resetWizard();
    ensureCsrf();

    const commentId = document.getElementById(
      "report-comment-id",
    ) as HTMLInputElement | null;
    const phoneNumber = document.getElementById(
      "report-phone-number",
    ) as HTMLInputElement | null;

    if (commentId) {
      commentId.value = trigger.dataset.commentId || "";
    }
    if (phoneNumber) {
      phoneNumber.value = trigger.dataset.phoneNumber || "";
    }

    lastTriggerButton = trigger;
    modal.show(trigger);
  });

  modalElement.addEventListener("shown.bs.modal", () => {
    focusFirstControl(currentStep);
  });

  modalElement.addEventListener("hidden.bs.modal", () => {
    requestGeneration++;
    activeRequest?.abort();
    activeRequest = null;

    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    resetReportTurnstile();
    resetWizard();
    lastTriggerButton?.focus({ preventScroll: true });
  });

  modalElement.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;

    if (target.closest(".report-next-btn") && validateStep(currentStep)) {
      showStep(currentStep + 1);
    } else if (target.closest(".report-back-btn")) {
      showStep(currentStep - 1);
    } else if (target.closest(".report-clear-btn")) {
      clearCurrentStep();
    }
  });

  otherCheckbox?.addEventListener("change", () => {
    otherHint?.classList.toggle("d-none", !otherCheckbox.checked);
  });

  window.addEventListener("turnstile-ready", () => {
    if (currentStep === 3 && modalElement.classList.contains("show")) {
      ensureReportTurnstile();
    }
  });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    clearErrors();

    const declaration = document.getElementById(
      "report-declaration",
    ) as HTMLInputElement | null;
    const turnstile = getTurnstile();
    let valid = true;

    if (!declaration?.checked) {
      showFieldError(
        "declaration",
        "You must confirm the declaration before submitting.",
      );
      declaration?.focus();
      valid = false;
    }

    if (!turnstile || reportWidgetId === null) {
      showFieldError(
        "cf-turnstile-response",
        "Verification is still loading. Please wait a moment and try again.",
      );
      valid = false;
    }

    const turnstileToken =
      turnstile && reportWidgetId !== null
        ? turnstile.getResponse(reportWidgetId)
        : "";

    if (!turnstileToken) {
      showFieldError(
        "cf-turnstile-response",
        "Please complete the verification challenge.",
      );
      valid = false;
    }

    if (!valid) {
      return;
    }

    await ensureCsrf();

    const csrfField = getCsrfField();
    const csrfHash = csrfField?.value || reportWindow.sec_token || "";
    const formData = new FormData(form);
    formData.set("cf-turnstile-response", turnstileToken);
    const generation = requestGeneration;
    const requestController = new AbortController();
    activeRequest = requestController;

    submitting = true;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = "Submitting…";
    }

    try {
      const response = await fetch("/phone/comment/report/", {
        method: "POST",
        body: formData,
        credentials: "same-origin",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-TOKEN": csrfHash,
        },
        signal: requestController.signal,
      });
      const data = (await response.json().catch(() => ({}))) as ReportResponse;

      if (generation !== requestGeneration) {
        return;
      }

      handleResponse(response.status, data);
    } catch (error) {
      if (
        generation !== requestGeneration ||
        (error instanceof DOMException && error.name === "AbortError")
      ) {
        return;
      }

      handleResponse(0, {
        message: "Something went wrong. Please try again.",
      });
    } finally {
      if (generation === requestGeneration) {
        activeRequest = null;
        submitting = false;
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = "Submit Report";
        }
      }
    }
  });
}
